import React, { FC } from 'react';
import CurrentConditions from '../components/CurrentConditions/CurrentConditions';
import GraphCards from '../components/GraphCards/GraphCards';
import { GraphTimeProvider } from '../components/GraphCards/GraphTimeContext';
import MoonPhaseCard from '../components/MoonPhaseCard/MoonPhaseCard';
import RiskForecast from '../components/RiskForecast/RiskForecast';
import SettingsLink from '../components/SettingsLink/SettingsLink';
import Warnings from '../components/Warnings/Warnings';
import { type WeatherForecastDaySelectHandler } from '../helpers/analytics/weatherForecastAnalytics';
import { type WeatherForecastMoonPhaseDay } from '../helpers/moon/formatMoonPhases';
import { type WeatherForecastDay } from '../helpers/outlook/formatDays';
import { FREE_FORECAST_DAYS } from '../helpers/forecastHorizon';
import { type WeatherForecastMoonPhasesError } from '../hooks/useWeatherForecastMoonPhases';

export interface WeatherForecastDayContentProps {
  latitude: number;
  longitude: number;
  selectedDate: string;
  selectedForecastDay: WeatherForecastDay;
  timeZone?: string;
  forecastDays: WeatherForecastDay[];
  moonPhaseDays: WeatherForecastMoonPhaseDay[];
  moonPhasesLoading: boolean;
  moonPhasesError: WeatherForecastMoonPhasesError | null;
  onSelectDay: WeatherForecastDaySelectHandler;
  forecastDaysRequested?: number;
  /**
   * Focused day in the swipe pager. Inactive (peek) panes still render cards
   * so adjacent days look complete while dragging; interaction and scrub-time
   * refresh stay gated to the focused pane.
   */
  isActive?: boolean;
}

const WeatherForecastDayContent: FC<WeatherForecastDayContentProps> = ({
  latitude,
  longitude,
  selectedDate,
  selectedForecastDay,
  timeZone,
  forecastDays,
  moonPhaseDays,
  moonPhasesLoading,
  moonPhasesError,
  onSelectDay,
  forecastDaysRequested = FREE_FORECAST_DAYS,
  isActive = true,
}) => (
  <>
    <Warnings latitude={latitude} longitude={longitude} />
    {selectedForecastDay.isToday && (
      <CurrentConditions latitude={latitude} longitude={longitude} />
    )}
    <GraphTimeProvider
      selectedDate={selectedDate}
      timeZone={timeZone}
      isToday={selectedForecastDay.isToday}
      isActive={isActive}
    >
      <RiskForecast
        latitude={latitude}
        longitude={longitude}
        selectedDate={selectedDate}
        weatherIcon={selectedForecastDay.weatherIcon}
        days={forecastDaysRequested}
      />
      <GraphCards
        latitude={latitude}
        longitude={longitude}
        selectedDate={selectedDate}
        isToday={selectedForecastDay.isToday}
        forecastDays={forecastDays}
        onSelectDay={isActive ? onSelectDay : undefined}
        days={forecastDaysRequested}
      />
    </GraphTimeProvider>
    <MoonPhaseCard
      moonPhaseDays={moonPhaseDays}
      selectedDate={selectedDate}
      onSelectDay={isActive ? onSelectDay : undefined}
      isLoading={moonPhasesLoading}
      error={moonPhasesError}
    />
    {isActive ? <SettingsLink /> : null}
  </>
);

export default WeatherForecastDayContent;
